import React, { Suspense, useEffect } from 'react';
import { RouteObject, Outlet, useLocation, useParams, Navigate } from 'react-router-dom';

import { ParentStatusRollupProvider } from './components/Activities/ParentStatusRollupPrompt.js';
import { AuthUnavailableNotice } from './components/AuthUnavailableNotice.js';
import { RouteErrorBoundary } from './components/ErrorBoundary/RouteErrorBoundary.js';
import ProtectedRoute from './components/ProtectedRoute.js';
import { PartialReceiptNotesProvider } from './components/PurchaseOrders/PartialReceiptNotesPrompt.js';
import { PoStatusRollupProvider } from './components/PurchaseOrders/PoStatusRollupPrompt.js';
import { ThemedLayout, AuthLayout, ThemedCard } from './components/themed/index.js';
import { lazyWithRetry as lazy } from './lib/lazyWithRetry.js';
import { useAppSelector } from './lib/reduxHooks.js';
import { selectUser, selectAuthStatus } from './store/slices/auth.js';
import OffsetContent from './components/OffsetContent.js';
import { useAuth } from './hooks/useAuth.js';
import { useCanViewSecurityPage } from './hooks/useCanViewSecurityPage.js';
// eslint-disable-next-line import/order -- grouped with the adjacent ./hooks/* imports, which already predate this file's import/order debt
import { useIsMobileOnly } from './hooks/useIsMobileOnly.js';
import { isStandalone } from './field/shared/useInstallPrompt.js';
import { isReleaseNotesEmailAllowlisted } from './lib/releaseNotesAllowlist.js';

// ============================================================================
// LAZY-LOADED PAGE COMPONENTS (Code Splitting)
// ============================================================================

// Auth & Core Pages
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage.js'));
const LoginPage = lazy(() => import('./pages/LoginPage.js'));
const RequestAccessPage = lazy(() => import('./pages/RequestAccessPage.js'));
const RequestAccessConfirmPage = lazy(() => import('./pages/RequestAccessConfirmPage.js'));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage.js'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage.js'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage.js'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage.js'));
const HomePage = lazy(() => import('./pages/HomePage.js'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js'));
const CatchUpPage = lazy(() => import('./pages/CatchUpPage.js'));

// Help (guides served by the login-only /api/v1/help API; card NKK-Iiu_wNBa)
const HelpIndex = lazy(() => import('./help/HelpIndex.js'));
const HelpGuide = lazy(() => import('./help/HelpGuide.js'));

// Job & Project Pages
const JobViewPage = lazy(() => import('./pages/JobViewPage.js'));
// Projects View disabled - uncomment to re-enable
// const ProjectsViewPage = lazy(() => import('./pages/ProjectsViewPage.js'));
const LotsViewPage = lazy(() => import('./pages/LotsViewPage.js'));
const BuildersViewPage = lazy(() => import('./pages/BuildersViewPage.js'));
const ActiveJobTrackerPage = lazy(() => import('./pages/ActiveJobTrackerPage.js'));

// Activities Pages
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage.js'));
const ActivitiesTrackerPage = lazy(() => import('./pages/ActivitiesTrackerPage.js'));

// Production Tracker (lazy so its @react-pdf/renderer dependency chain is not
// welded into the main bundle that loads on every route — named export mapped to default)
const ProductionTrackerDashboard = lazy(() =>
  import('./components/ProductionTracker/ProductionTrackerDashboard.js').then((m) => ({
    default: m.ProductionTrackerDashboard,
  }))
);

// Inventory & Materials Pages
const InventoryPage = lazy(() => import('./pages/InventoryPage.js'));
const InventoryDetailsPage = lazy(() => import('./pages/InventoryDetailsPage.js'));
const InventoryActivityPage = lazy(() => import('./pages/InventoryActivityPage.js'));
const MaterialsPage = lazy(() => import('./pages/MaterialsPage.js'));
const PricebooksPage = lazy(() => import('./pages/PricebooksPage.js'));
const PricebookIngestionPage = lazy(() => import('./pages/PricebookIngestionPage.js'));

// Purchase Order Pages
const PurchaseOrdersPage = lazy(() => import('./pages/purchase-orders.js'));
const PurchaseOrderCreatePage = lazy(() => import('./pages/PurchaseOrderCreatePage.js'));
const PhasePurchaseOrderPage = lazy(() => import('./pages/PhasePurchaseOrderPage.js'));
const PurchaseOrderTrackerPage = lazy(() => import('./pages/PurchaseOrderTrackerPage.js'));

// Production Pages
const ProductionOverviewPage = lazy(() => import('./pages/ProductionOverviewPage.js'));
const ProductionManagerPage = lazy(() => import('./pages/ProductionManagerPage.js'));

// Builder Pages
const BuilderTrackerPage = lazy(() => import('./pages/BuilderTrackerPage.js'));

// Vendors & Templates Pages
const VendorsPage = lazy(() => import('./pages/VendorsPage.js'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage.js'));
const TemplateAreasPage = lazy(() => import('./pages/TemplateAreasPage.js'));
const TemplateActivitiesPage = lazy(() => import('./pages/TemplateActivitiesPage.js'));

// Settings Pages
const ProfilePage = lazy(() => import('./pages/ProfilePage.js'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage.js'));
const SettingsPage = lazy(() => import('./pages/SettingsPage.js'));
const OrganizationSettingsPage = lazy(() => import('./pages/OrganizationSettingsPage.js'));
const OrganizationDetailsSettingsPage = lazy(() => import('./pages/OrganizationDetailsSettingsPage.js'));
const RolePermissionsSettingsPage = lazy(() => import('./pages/RolePermissionsSettingsPage.js'));
const UserManagementSettingsPage = lazy(() => import('./pages/UserManagementSettingsPage.js'));
const WarehouseWorkersSettingsPage = lazy(() => import('./pages/WarehouseWorkersSettingsPage.js'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage.js'));
const TestAccountsSettingsPage = lazy(() => import('./pages/TestAccountsSettingsPage.js'));
const StorageMonitoringSettingsPage = lazy(() => import('./pages/StorageMonitoringSettingsPage.js'));
const ImportHistorySettingsPage = lazy(() => import('./pages/ImportHistorySettingsPage.js'));
const ProjectConfigSettingsPage = lazy(() => import('./pages/ProjectConfigSettingsPage.js'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage.js'));

const ProjectDefaultsSettingsPage = lazy(() => import('./pages/ProjectDefaultsSettingsPage.js'));
const StatusConfigurationPage = lazy(() => import('./pages/StatusConfigurationPage.js'));
const TemplateManagementPage = lazy(() => import('./pages/TemplateManagementPage.js'));
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage.js'));
const POPrintPresetsPage = lazy(() => import('./pages/POPrintPresetsPage.js'));
const PaymentTermsSettingsPage = lazy(() => import('./pages/PaymentTermsSettingsPage.js'));
const PricebookMappingSettingsPage = lazy(() => import('./pages/PricebookMappingSettingsPage.js'));
const PricebookImportMappingsSettingsPage = lazy(() => import('./pages/PricebookImportMappingsSettingsPage.js'));

// Public marketing pages (no auth required)
const SecurityPage = lazy(() => import('./pages/SecurityPage.js'));

// Developer Pages
const DevPage = lazy(() => import('./pages/DevPage.js'));
const ApiExplorerPage = lazy(() => import('./pages/ApiExplorerPage.js'));
const EmailTestPage = lazy(() => import('./pages/EmailTestPage.js'));
const DatabaseInspectorPage = lazy(() => import('./pages/DatabaseInspectorPage.js'));
const TestingDashboard = lazy(() => import('./pages/dev/TestingDashboard.js'));
const SecurityPostureDashboard = lazy(() => import('./pages/dev/SecurityPostureDashboard.js'));
const DebugPage = lazy(() => import('./pages/DebugPage.js'));
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage.js'));

// Field PWA — separate route tree at /field/* with its own layout, scope, and manifest.
// All field components live under client/field/ and are pulled in via fieldRoutes
// which is itself lazy-imported below to keep them out of the desktop bundle.
import { fieldRoutes } from './field/routes.js';

// ============================================================================
// ROUTES CONFIGURATION
// ============================================================================

/**
 * Shared route definitions for both client and server
 */
export const routes: RouteObject[] = [
  {
    id: 'root',
    path: '/',
    element: (
      <RootLayoutWithUser />
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <HomePageWrapper />,
        handle: { title: 'Home' },
      },
      {
        path: "job-view",
        element: <JobViewWrapper />,
        handle: { title: 'Job View' },
      },
      {
        path: "job-view/:jobId",
        element: <JobViewWrapper />,
        handle: { title: 'Job Details' },
      },
      // Projects View disabled - uncomment to re-enable
      // {
      //   path: "projects-view",
      //   element: <ProjectsViewWrapper />,
      //   handle: { title: 'Projects View' },
      // },
      // {
      //   path: "projects-view/:projectId",
      //   element: <ProjectsViewWrapper />,
      //   handle: { title: 'Project Details' },
      // },
      {
        path: "lots",
        element: <LotsViewWrapper />,
        handle: { title: 'Lots View' },
      },
      {
        path: "lots/:lotId",
        element: <LotsViewWrapper />,
        handle: { title: 'Lot Details' },
      },
      {
        path: "builders",
        element: <BuilderTrackerPageWrapper />,
        handle: { title: 'Builder Tracker' },
      },
      {
        path: "builders/:builderId",
        element: <BuildersViewWrapper />,
        handle: { title: 'Builder Details' },
      },
      {
        path: "user/:userId",
        element: <UserProfilePageWrapper />,
        handle: { title: 'User Profile' },
      },
      {
        path: "activities",
        element: <ActivitiesPageWrapper />,
        handle: { title: 'Activities' },
      },
      {
        path: "activities-tracker",
        element: <ActivitiesTrackerPageWrapper />,
        handle: { title: 'Activities Tracker' },
      },
      {
        path: "inventory",
        element: <InventoryPageWrapper />,
        handle: { title: 'Inventory' },
      },
      {
        path: "inventory/activity",
        element: <InventoryActivityPageWrapper />,
        handle: { title: 'Inventory Activity' },
      },
      {
        path: "inventory/:id",
        element: <InventoryDetailsPageWrapper />,
        handle: { title: 'Inventory Details' },
      },
      {
        path: "materials",
        element: <MaterialsPageWrapper />,
        handle: { title: 'Materials' },
      },
      {
        path: "pricebooks",
        element: <PricebooksPageWrapper />,
        handle: { title: 'Pricebooks' },
      },
      {
        path: "pricebooks/ingestion",
        element: <PricebookIngestionPageWrapper />,
        handle: { title: 'Pricebook Ingestion' },
      },
      {
        path: "purchase-orders",
        element: <PurchaseOrdersPageWrapper />,
        handle: { title: 'Purchase Orders' },
      },
      {
        path: "purchase-orders/create",
        element: <PurchaseOrderCreatePageWrapper />,
        handle: { title: 'Create Purchase Order' },
      },
      {
        path: "purchase-orders/phase",
        element: <PhasePurchaseOrderPageWrapper />,
        handle: { title: 'Create Purchase Order' },
      },
      {
        path: "purchase-orders/phase/:id",
        element: <PhasePurchaseOrderPageWrapper />,
        handle: { title: 'Edit Purchase Order' },
      },
      {
        path: "purchase-orders/tracker",
        element: <PurchaseOrderTrackerPageWrapper />,
        handle: { title: 'Purchase Order Tracker' },
      },
      {
        path: "production-tracker",
        element: <ProductionTrackerPageWrapper />,
        handle: { title: 'Production Tracker' },
      },
      {
        path: "production-overview",
        element: <ProductionOverviewPageWrapper />,
        handle: { title: 'Production Overview' },
      },
      {
        path: "production-manager",
        element: <ProductionManagerPageWrapper />,
        handle: { title: 'Production Manager' },
      },
      {
        path: "job-tracker",
        element: <ActiveJobTrackerPageWrapper />,
        handle: { title: 'Active Job Tracker' },
      },
      {
        // Deprecated — the tracker's own job page was replaced by Job View
        // (0484). Old links/bookmarks redirect to /job-view/:jobId.
        path: "job-tracker/:jobId",
        element: <JobTrackerDetailRedirect />,
      },
      {
        // Legacy alias — older bookmarks and external docs point at
        // /active-job-tracker. Canonical route is /job-tracker.
        // Browser-testing U1 surfaced the 404. Cheap one-line redirect
        // keeps stale links working; rename the canonical route later
        // if /job-tracker stops matching the page's display name.
        path: "active-job-tracker",
        element: <Navigate to="/job-tracker" replace />,
      },
      {
        // Legacy alias — release notes and docs point at /jobs, but the job
        // list lives at /job-view (remote test pass surfaced the 404).
        path: "jobs",
        element: <Navigate to="/job-view" replace />,
      },
      {
        // /jobs/:jobId → /job-view/:jobId (same redirect the retired
        // tracker job page uses).
        path: "jobs/:jobId",
        element: <JobTrackerDetailRedirect />,
      },
      {
        path: "vendors",
        element: <VendorsPageWrapper />,
        handle: { title: 'Vendors' },
      },
      {
        path: "templates",
        element: <TemplatesPageWrapper />,
        handle: { title: 'Templates' },
      },
      {
        path: "templates/areas",
        element: <TemplateAreasPageWrapper />,
        handle: { title: 'Template Areas' },
      },
      {
        path: "templates/activities",
        element: <TemplateActivitiesPageWrapper />,
        handle: { title: 'Template Activities' },
      },
      {
        // Consolidated into "Organization & Users". The standalone Organization
        // page (org details + team table + invite modal) was folded into the
        // canonical settings pages: org details → /settings/organization/details,
        // the invite + user table + deactivate/reactivate → User Management.
        // Redirect keeps old bookmarks / external links working.
        path: "organization",
        element: <Navigate to="/settings/organization/user-management" replace />,
      },
      {
        path: "settings",
        element: <SettingsPageWrapper />,
        handle: { title: 'Settings' },
      },
      {
        path: "settings/organization",
        element: <OrganizationSettingsPageWrapper />,
        handle: { title: 'Organization Settings' },
      },
      {
        path: "settings/organization/details",
        element: <OrganizationDetailsSettingsPageWrapper />,
        handle: { title: 'Organization Details' },
      },
      {
        path: "settings/organization/role-permissions",
        element: <RolePermissionsSettingsPageWrapper />,
        handle: { title: 'Role Permissions' },
      },
      {
        path: "settings/organization/user-management",
        element: <UserManagementSettingsPageWrapper />,
        handle: { title: 'User Management' },
      },
      {
        path: "settings/organization/warehouse-workers",
        element: <WarehouseWorkersSettingsPageWrapper />,
        handle: { title: 'Warehouse Workers' },
      },
      {
        path: "settings/admin",
        element: <AdminSettingsPageWrapper />,
        handle: { title: 'Admin Settings' },
      },
      {
        path: "settings/admin/test-accounts",
        element: <TestAccountsSettingsPageWrapper />,
        handle: { title: 'Test Accounts' },
      },
      {
        path: "settings/admin/storage",
        element: <StorageMonitoringSettingsPageWrapper />,
        handle: { title: 'Storage Monitoring' },
      },
      {
        path: "settings/imports",
        element: <ImportHistorySettingsPageWrapper />,
        handle: { title: 'Import History' },
      },
      {
        path: "settings/project-config",
        element: <ProjectConfigSettingsPageWrapper />,
        handle: { title: 'Project Configuration' },
      },
      {
        path: "settings/notifications",
        element: <NotificationSettingsPageWrapper />,
        handle: { title: 'Notification Preferences' },
      },
      {
        // Focused "Catch Up" notification card stack (Slack-style walk-the-stack).
        // Deep-linkable route; also openable inline from the notification bell.
        path: "notifications/catch-up",
        element: <CatchUpPageWrapper />,
        handle: { title: 'Catch Up' },
      },
      {
        // Help section: every logged-in role (no permission gate).
        path: "help",
        element: <HelpIndexWrapper />,
        handle: { title: 'Help' },
      },
      {
        path: "help/:slug",
        element: <HelpGuideWrapper />,
        handle: { title: 'Help' },
      },
      {
        path: "settings/project-config/project-defaults",
        element: <ProjectDefaultsSettingsPageWrapper />,
        handle: { title: 'Project Defaults' },
      },
      {
        path: "settings/project-config/status-configurations",
        element: <StatusConfigurationPageWrapper />,
        handle: { title: 'Status Configurations' },
      },
      {
        path: "settings/project-config/template-management",
        element: <TemplateManagementPageWrapper />,
        handle: { title: 'Template Management' },
      },
      {
        path: "settings/project-config/template-management/new",
        element: <TemplateEditorPageWrapper />,
        handle: { title: 'Create Template' },
      },
      {
        path: "settings/project-config/template-management/:templateId/edit",
        element: <TemplateEditorPageWrapper />,
        handle: { title: 'Edit Template' },
      },
      {
        path: "settings/project-config/po-print-presets",
        element: <POPrintPresetsPageWrapper />,
        handle: { title: 'PO Print Presets' },
      },
      {
        path: "settings/project-config/payment-terms",
        element: <PaymentTermsSettingsPageWrapper />,
        handle: { title: 'Payment Terms' },
      },
      {
        path: "settings/project-config/pricebook-material-mapping",
        element: <PricebookMappingSettingsPageWrapper />,
        handle: { title: 'Pricebook → Material Mapping' },
      },
      {
        path: "settings/project-config/pricebook-import-mappings",
        element: <PricebookImportMappingsSettingsPageWrapper />,
        handle: { title: 'Pricebook Import Mappings' },
      },
      {
        path: "profile",
        element: <ProfilePageWrapper />,
        handle: { title: 'Profile' },
      },
      {
        path: "login",
        element: <LoginPageWrapper />,
        handle: { title: 'Login' },
      },
      // Accounts come only from an invite or an approved access request
      // (card xhNJ9dCqsAzP): the old self-signup pages called /auth/signup,
      // which needs a login since the global guard. Keep the URLs working.
      {
        path: "register",
        element: <Navigate to="/request-access" replace />,
      },
      {
        path: "signup",
        element: <Navigate to="/request-access" replace />,
      },
      {
        path: "request-access",
        element: <RequestAccessPageWrapper />,
        handle: { title: 'Request access' },
      },
      {
        path: "request-access/confirm",
        element: <RequestAccessConfirmPageWrapper />,
        handle: { title: 'Confirm request' },
      },
      {
        path: "onboarding",
        element: <OnboardingPageWrapper />,
        handle: { title: 'Welcome' },
      },
      {
        path: "auth/callback",
        element: <AuthCallbackPageWrapper />,
        // No title needed for callback page
      },
      {
        path: "security",
        element: <SecurityPageWrapper />,
        handle: { title: 'Security' },
      },
      {
        path: "set-password",
        element: <SetPasswordPageWrapper />,
        handle: { title: 'Set Password' },
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPageWrapper />,
        handle: { title: 'Forgot Password' },
      },
      {
        path: "reset-password",
        element: <ResetPasswordPageWrapper />,
        handle: { title: 'Reset Password' },
      },
      {
        path: "dev",
        element: <DevPageWrapper />,
        handle: { title: 'Developer Tools' },
      },
      {
        path: "dev/api-explorer",
        element: <ApiExplorerPageWrapper />,
        handle: { title: 'API Explorer' },
      },
      {
        path: "dev/api-explorer/email",
        element: <EmailTestPageWrapper />,
        handle: { title: 'Email API Test' },
      },
      {
        path: "dev/database",
        element: <DatabaseInspectorPageWrapper />,
        handle: { title: 'Database Inspector' },
      },
      {
        path: "dev/performance",
        element: <TestingDashboardWrapper />,
        handle: { title: 'Performance Testing' },
      },
      {
        path: "dev/security",
        element: <SecurityPostureDashboardWrapper />,
        handle: { title: 'Security Posture' },
      },
      {
        path: "debug",
        element: <DebugPageWrapper />,
        handle: { title: 'Debug' },
      },
      {
        path: "devtools/release-notes",
        element: <ReleaseNotesPageWrapper />,
        handle: { title: 'Release Notes' },
      },
      {
        path: "*",
        element: <NotFoundPageWrapper />,
        handle: { title: 'Not Found' },
      }
    ]
  },
  // Field PWA routes (separate from the desktop tree). Kept as siblings so
  // /field has its own layout, auth guard, manifest scope, and bottom tab bar.
  ...fieldRoutes,
];

// ============================================================================
// PUBLIC ROUTES
// ============================================================================

/**
 * List of public routes that don't require authentication
 */
const PUBLIC_ROUTES = [
  '/login',
  '/signup',
  '/register',
  '/request-access',
  '/auth/callback',
  '/set-password',
  '/forgot-password',
  '/reset-password',
  '/field/login',
  '/security',
];

/**
 * Public routes that should NOT use the AuthLayout split-panel form chrome.
 * These render in their own self-contained layout (e.g. marketing pages).
 */
const MARKETING_ROUTES = [
  '/security',
];

// ============================================================================
// ROOT LAYOUT
// ============================================================================

/**
 * Root layout with authentication protection
 * All routes except PUBLIC_ROUTES require authentication
 * Uses AuthLayout for public routes (login, signup) and ThemedLayout for protected routes
 */
function RootLayoutWithUser() {
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const authStatus = useAppSelector(selectAuthStatus);
  const isMobileOnly = useIsMobileOnly();

  // Check if current route is public
  const isPublicRoute = PUBLIC_ROUTES.some(route =>
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Loading state component
  const LoadingComponent = () => (
    <OffsetContent offsetRatio={0.1}>
      <ThemedCard className="p-8">
        <div className="text-center">
          <div className="text-lg">Loading...</div>
        </div>
      </ThemedCard>
    </OffsetContent>
  );

  // Wait for auth to complete before making any decisions.
  // 'unavailable' is not "still loading" — it is a terminal answer of "we
  // couldn't ask", handled separately below so we neither spin forever nor
  // bounce a signed-in user to /login.
  const authLoading =
    authStatus !== 'succeeded' && authStatus !== 'failed' && authStatus !== 'unavailable';

  // Debug logging for auth state (in useEffect to avoid setState during render)
  useEffect(() => {
    console.log('[RootLayoutWithUser] Auth State:', {
      authStatus,
      hasUser: !!user,
      userEmail: user?.email,
      isPublicRoute,
      authLoading,
      pathname: location.pathname
    });
  }, [authStatus, user, isPublicRoute, authLoading, location.pathname]);

  useEffect(() => {
    if (authLoading && !isPublicRoute) {
      console.log('[RootLayoutWithUser] Showing loading screen - auth not complete');
    }
  }, [authLoading, isPublicRoute]);

  // Belt-and-suspenders for the Supabase password-reset flow: when Supabase's
  // Site-URL fallback dumps a recovery token in the hash on some other path
  // (typically `/` because the redirect_to path wasn't allowlisted), route to
  // /reset-password *before* the auth guard runs — otherwise <Navigate to="/login">
  // would drop the hash and the tokens with it.
  // Must live after every hook call so the hook order stays stable across renders.
  if (
    location.pathname !== '/reset-password' &&
    location.hash.includes('type=recovery')
  ) {
    return <Navigate to={{ pathname: '/reset-password', hash: location.hash }} replace />;
  }

  // Installed-PWA launches ALWAYS open the field app (Noah, 2026-08-26). The
  // desktop tree is office-only; an installed (standalone) launch is a field
  // worker, so bounce any desktop-tree route to the field shell — regardless of
  // auth state or role. /field/* is a SIBLING route tree with its own guard, so
  // this never re-enters here (no redirect loop). This covers the launch paths
  // the role-guard below misses (logged-out, `both`-role, pre-hydration) and the
  // manifest start_url gaps (existing installs whose cached start_url is /).
  // isStandalone() is false in a normal browser tab, so office web use is
  // untouched — only the installed icon is affected.
  // Exception: /request-access (public; reached from the field login's
  // "Request access" link, so it must open inside the installed app too).
  if (
    isStandalone() &&
    !location.pathname.startsWith('/field') &&
    !location.pathname.startsWith('/request-access')
  ) {
    return <Navigate to="/field/home" replace />;
  }

  if (authLoading && !isPublicRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <LoadingComponent />
      </div>
    );
  }

  // Backend unreachable and we have no user to fall back on. The session
  // cookie is untouched, so this is NOT a logout — hold on a self-retrying
  // reconnect screen instead of redirecting to /login. This is the redeploy
  // case: sending them to /login here is what silently ends valid sessions.
  if (authStatus === 'unavailable' && !(user && user.id) && !isPublicRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <AuthUnavailableNotice />
      </div>
    );
  }

  // Redirect to login if no authenticated user
  const isValidUser = user && user.id;
  if (!isValidUser && !isPublicRoute && (authStatus === 'succeeded' || authStatus === 'failed')) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Field→office redirect guard (defense in depth): a mobile-only (field_*)
  // user who ends up on a non-/field path — stale bookmark, typed URL, a
  // link from elsewhere in the app — gets bounced to the field shell. This
  // is UX; the server is the real enforcement boundary. Office users are
  // unaffected (isMobileOnly is false for them).
  if (isValidUser && isMobileOnly && !location.pathname.startsWith('/field')) {
    return <Navigate to="/field/home" replace />;
  }

  // Marketing-style public routes render their own full-page layout — no
  // AuthLayout chrome, no sidebar. Used for things like the public /security
  // page that need long-form content.
  const isMarketingRoute = MARKETING_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`),
  );
  if (isMarketingRoute) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
        <Outlet />
      </Suspense>
    );
  }

  // Use AuthLayout for public routes (clean, no sidebar)
  if (isPublicRoute) {
    return (
      <AuthLayout>
        <Suspense fallback={<div className="text-center">Loading...</div>}>
          <Outlet />
        </Suspense>
      </AuthLayout>
    );
  }

  // Use ThemedLayout for protected routes (with sidebar and header)
  return (
    <ThemedLayout>
      {/* Card UD7blPRrNa0C: one app-level host for the "set the parent too?"
          prompt, so it outlives the row / batch bar that triggered it. */}
      <ParentStatusRollupProvider>
        {/* Card i-salOBh3pft: same idea for "set the PO too?" after line
            status changes. */}
        <PoStatusRollupProvider>
          {/* Card 8reidTon-4MB: "save a partial-receipt note?" after Receive
              Items, which unmounts on close at every entry point. */}
          <PartialReceiptNotesProvider>
            <Suspense fallback={<LoadingComponent />}>
              <Outlet />
            </Suspense>
          </PartialReceiptNotesProvider>
        </PoStatusRollupProvider>
      </ParentStatusRollupProvider>
    </ThemedLayout>
  );
}

// ============================================================================
// WRAPPER COMPONENTS (Suspense Boundaries)
// ============================================================================

function HomePageWrapper() {
  return (
    <Suspense fallback={<div>Loading home...</div>}>
      <HomePage />
    </Suspense>
  );
}

function JobViewWrapper() {
  return (
    <Suspense fallback={<div>Loading job view...</div>}>
      <JobViewPage />
    </Suspense>
  );
}

// Projects View disabled - uncomment to re-enable
// function ProjectsViewWrapper() {
//   return (
//     <Suspense fallback={<div>Loading projects...</div>}>
//       <ProjectsViewPage />
//     </Suspense>
//   );
// }

function LotsViewWrapper() {
  const location = useLocation();
  return (
    <Suspense fallback={<div>Loading lots...</div>}>
      <LotsViewPage key={location.key} />
    </Suspense>
  );
}

function BuildersViewWrapper() {
  return (
    <Suspense fallback={<div>Loading builders...</div>}>
      <BuildersViewPage />
    </Suspense>
  );
}

function ActivitiesPageWrapper() {
  return (
    <Suspense fallback={<div>Loading activities...</div>}>
      <ActivitiesPage />
    </Suspense>
  );
}

function ActivitiesTrackerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading activities tracker...</div>}>
      <ActivitiesTrackerPage />
    </Suspense>
  );
}

function ProfilePageWrapper() {
  return (
    <Suspense fallback={<div>Loading profile...</div>}>
      <ProfilePage />
    </Suspense>
  );
}

function UserProfilePageWrapper() {
  return (
    <Suspense fallback={<div>Loading user profile...</div>}>
      <UserProfilePage />
    </Suspense>
  );
}

function TemplatesPageWrapper() {
  return (
    <Suspense fallback={<div>Loading templates...</div>}>
      <TemplatesPage />
    </Suspense>
  );
}

function TemplateAreasPageWrapper() {
  return (
    <Suspense fallback={<div>Loading template areas...</div>}>
      <TemplateAreasPage />
    </Suspense>
  );
}

function TemplateActivitiesPageWrapper() {
  return (
    <Suspense fallback={<div>Loading template activities...</div>}>
      <TemplateActivitiesPage />
    </Suspense>
  );
}

function SettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading settings...</div>}>
      <SettingsPage />
    </Suspense>
  );
}

function OrganizationSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading organization settings...</div>}>
      <OrganizationSettingsPage />
    </Suspense>
  );
}

function OrganizationDetailsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading organization details...</div>}>
      <OrganizationDetailsSettingsPage />
    </Suspense>
  );
}

function RolePermissionsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading role permissions...</div>}>
      <RolePermissionsSettingsPage />
    </Suspense>
  );
}

function UserManagementSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading user management...</div>}>
      <UserManagementSettingsPage />
    </Suspense>
  );
}

function WarehouseWorkersSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading warehouse workers...</div>}>
      <WarehouseWorkersSettingsPage />
    </Suspense>
  );
}

function AdminSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading admin settings...</div>}>
      <AdminSettingsPage />
    </Suspense>
  );
}

function TestAccountsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading test accounts...</div>}>
      <TestAccountsSettingsPage />
    </Suspense>
  );
}

function StorageMonitoringSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading storage monitoring...</div>}>
      <StorageMonitoringSettingsPage />
    </Suspense>
  );
}

function ImportHistorySettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading import history...</div>}>
      <ImportHistorySettingsPage />
    </Suspense>
  );
}

function ProjectConfigSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading project config...</div>}>
      <ProjectConfigSettingsPage />
    </Suspense>
  );
}

function NotificationSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading notification settings...</div>}>
      <NotificationSettingsPage />
    </Suspense>
  );
}

function CatchUpPageWrapper() {
  return (
    <Suspense fallback={<div>Loading catch up...</div>}>
      <CatchUpPage />
    </Suspense>
  );
}

function HelpIndexWrapper() {
  return (
    <Suspense fallback={<div>Loading help...</div>}>
      <HelpIndex />
    </Suspense>
  );
}

function HelpGuideWrapper() {
  return (
    <Suspense fallback={<div>Loading help...</div>}>
      <HelpGuide />
    </Suspense>
  );
}


function StatusConfigurationPageWrapper() {
  return (
    <Suspense fallback={<div>Loading status configuration...</div>}>
      <StatusConfigurationPage />
    </Suspense>
  );
}

function TemplateManagementPageWrapper() {
  return (
    <Suspense fallback={<div>Loading template management...</div>}>
      <TemplateManagementPage />
    </Suspense>
  );
}

function POPrintPresetsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading PO print presets...</div>}>
      <POPrintPresetsPage />
    </Suspense>
  );
}

function PaymentTermsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading payment terms...</div>}>
      <PaymentTermsSettingsPage />
    </Suspense>
  );
}

function PricebookMappingSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading pricebook → material mapping...</div>}>
      <PricebookMappingSettingsPage />
    </Suspense>
  );
}

function PricebookImportMappingsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading pricebook import mappings...</div>}>
      <PricebookImportMappingsSettingsPage />
    </Suspense>
  );
}

function TemplateEditorPageWrapper() {
  return (
    <Suspense fallback={<div>Loading template editor...</div>}>
      <TemplateEditorPage />
    </Suspense>
  );
}

function ProjectDefaultsSettingsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading project defaults...</div>}>
      <ProjectDefaultsSettingsPage />
    </Suspense>
  );
}

function InventoryPageWrapper() {
  return (
    <Suspense fallback={<div>Loading inventory...</div>}>
      <InventoryPage />
    </Suspense>
  );
}

function InventoryDetailsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading inventory details...</div>}>
      <InventoryDetailsPage />
    </Suspense>
  );
}

function InventoryActivityPageWrapper() {
  return (
    <Suspense fallback={<div>Loading inventory activity...</div>}>
      <InventoryActivityPage />
    </Suspense>
  );
}

function MaterialsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading materials...</div>}>
      <MaterialsPage />
    </Suspense>
  );
}

function PricebooksPageWrapper() {
  return (
    <Suspense fallback={<div>Loading pricebooks...</div>}>
      <PricebooksPage />
    </Suspense>
  );
}

function PricebookIngestionPageWrapper() {
  return (
    <Suspense fallback={<div>Loading pricebook ingestion...</div>}>
      <PricebookIngestionPage />
    </Suspense>
  );
}

function PurchaseOrdersPageWrapper() {
  return (
    <Suspense fallback={<div>Loading purchase orders...</div>}>
      <PurchaseOrdersPage />
    </Suspense>
  );
}

function VendorsPageWrapper() {
  return (
    <Suspense fallback={<div>Loading vendors...</div>}>
      <VendorsPage />
    </Suspense>
  );
}

function PurchaseOrderCreatePageWrapper() {
  return (
    <Suspense fallback={<div>Loading purchase order creator...</div>}>
      <PurchaseOrderCreatePage />
    </Suspense>
  );
}

function PhasePurchaseOrderPageWrapper() {
  const { id } = useParams();
  return (
    <Suspense fallback={<div>Loading purchase order...</div>}>
      <PhasePurchaseOrderPage key={id || 'new'} />
    </Suspense>
  );
}

function PurchaseOrderTrackerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading purchase order tracker...</div>}>
      <PurchaseOrderTrackerPage />
    </Suspense>
  );
}

function ProductionTrackerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading production tracker...</div>}>
      <ProductionTrackerDashboard />
    </Suspense>
  );
}

function ProductionOverviewPageWrapper() {
  return (
    <Suspense fallback={<div>Loading production overview...</div>}>
      <ProductionOverviewPage />
    </Suspense>
  );
}

function ProductionManagerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading production manager...</div>}>
      <ProductionManagerPage />
    </Suspense>
  );
}

function BuilderTrackerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading builder tracker...</div>}>
      <BuilderTrackerPage />
    </Suspense>
  );
}

function ActiveJobTrackerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading active job tracker...</div>}>
      <ActiveJobTrackerPage />
    </Suspense>
  );
}

function JobTrackerDetailRedirect() {
  const { jobId } = useParams<{ jobId: string }>();
  return <Navigate to={`/job-view/${jobId}`} replace />;
}

function OnboardingPageWrapper() {
  return (
    <Suspense fallback={<div>Loading onboarding...</div>}>
      <OnboardingPage />
    </Suspense>
  );
}

function LoginPageWrapper() {
  return (
    <Suspense fallback={<div>Loading login...</div>}>
      <LoginPage />
    </Suspense>
  );
}

function RequestAccessPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RequestAccessPage />
    </Suspense>
  );
}

function RequestAccessConfirmPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RequestAccessConfirmPage />
    </Suspense>
  );
}

function AuthCallbackPageWrapper() {
  return (
    <Suspense fallback={<div>Processing authentication...</div>}>
      <AuthCallbackPage />
    </Suspense>
  );
}

function SetPasswordPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SetPasswordPage />
    </Suspense>
  );
}

function ForgotPasswordPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ForgotPasswordPage />
    </Suspense>
  );
}

function ResetPasswordPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ResetPasswordPage />
    </Suspense>
  );
}

function DevPageWrapper() {
  return (
    <Suspense fallback={<div>Loading developer tools...</div>}>
      <DevPage />
    </Suspense>
  );
}

function SecurityPageWrapper() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <SecurityPage />
    </Suspense>
  );
}

function SecurityPostureDashboardWrapper() {
  // Internal page: gated by an env-driven allowlist of system-dev emails.
  // Returning <NotFoundPage /> instead of a redirect avoids confirming the
  // route exists to a viewer who lacks access.
  const allowed = useCanViewSecurityPage();
  if (!allowed) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <NotFoundPage />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<div>Loading security posture...</div>}>
      <SecurityPostureDashboard />
    </Suspense>
  );
}

function ApiExplorerPageWrapper() {
  return (
    <Suspense fallback={<div>Loading API explorer...</div>}>
      <ApiExplorerPage />
    </Suspense>
  );
}

function EmailTestPageWrapper() {
  return (
    <Suspense fallback={<div>Loading email test...</div>}>
      <EmailTestPage />
    </Suspense>
  );
}

function DatabaseInspectorPageWrapper() {
  return (
    <Suspense fallback={<div>Loading database inspector...</div>}>
      <DatabaseInspectorPage />
    </Suspense>
  );
}

function TestingDashboardWrapper() {
  return (
    <Suspense fallback={<div>Loading testing dashboard...</div>}>
      <TestingDashboard />
    </Suspense>
  );
}

function DebugPageWrapper() {
  return (
    <Suspense fallback={<div>Loading debug...</div>}>
      <DebugPage />
    </Suspense>
  );
}

function ReleaseNotesPageWrapper() {
  // Gate the route on the release-notes email allowlist. Belt + suspenders:
  // the sidebar already hides the nav item, and the backend still 403s if
  // the API is hit directly — this stops a curious signed-in-but-not-
  // allowlisted user from loading the page shell when they type the URL.
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <div>Loading…</div>;
  }
  if (!isReleaseNotesEmailAllowlisted(user?.email ?? null)) {
    // 404 (not 403) so we don't confirm the route exists to non-admins.
    return <NotFoundPageWrapper />;
  }
  return (
    <Suspense fallback={<div>Loading release notes...</div>}>
      <ReleaseNotesPage />
    </Suspense>
  );
}

function NotFoundPageWrapper() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <NotFoundPage />
    </Suspense>
  );
}
